import React, { useState, useRef, useEffect } from 'react';
import { Calendar, Clock, ChevronLeft, ChevronRight, X } from 'lucide-react';

const DateTimePicker = ({
  value = '',
  onChange = () => {},
  placeholder = 'Select date and time',
  minDate = null,
  maxDate = null,
  allowPastDates = true,
  disabled = false,
  required = false,
  label = '',
  error = '',
  className = '',
  size = 'md',
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDateTime, setSelectedDateTime] = useState(value ? new Date(value) : null);
  const [selectedTime, setSelectedTime] = useState({ hours: 0, minutes: 0 });
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [hoveredDate, setHoveredDate] = useState(null);
  const dateTimePickerRef = useRef(null);

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  };

  const baseClasses = 'block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 bg-white cursor-pointer';
  
  const inputClasses = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
    ${className}
  `.trim();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateTimePickerRef.current && !dateTimePickerRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowTimePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update selected date when value prop changes
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      setSelectedDateTime(date);
      setSelectedTime({
        hours: date.getHours(),
        minutes: date.getMinutes()
      });
    } else {
      setSelectedDateTime(null);
      setSelectedTime({ hours: 0, minutes: 0 });
    }
  }, [value]);

  const formatDisplayDateTime = (date) => {
    if (!date) return placeholder;
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (minDate && date < new Date(minDate)) return true;
    if (maxDate && date > new Date(maxDate)) return true;
    if (!allowPastDates && date < today) return true;
    
    return false;
  };

  const isDateSelected = (date) => {
    if (!date || !selectedDateTime) return false;
    return date.toDateString() === selectedDateTime.toDateString();
  };

  const isDateHovered = (date) => {
    if (!date || !hoveredDate) return false;
    return date.toDateString() === hoveredDate.toDateString();
  };

  const handleDateSelect = (date) => {
    if (!date || isDateDisabled(date)) return;
    
    const newDateTime = new Date(date);
    // Preserve existing time if available, otherwise use selectedTime
    if (selectedDateTime) {
      newDateTime.setHours(selectedDateTime.getHours(), selectedDateTime.getMinutes(), 0, 0);
      setSelectedTime({
        hours: selectedDateTime.getHours(),
        minutes: selectedDateTime.getMinutes()
      });
    } else {
      newDateTime.setHours(selectedTime.hours, selectedTime.minutes, 0, 0);
    }
    
    setSelectedDateTime(newDateTime);
    setShowTimePicker(true);
  };

  const handleTimeChange = (hours, minutes) => {
    const newTime = { hours, minutes };
    setSelectedTime(newTime);
    
    if (selectedDateTime) {
      const newDateTime = new Date(selectedDateTime);
      newDateTime.setHours(hours, minutes, 0, 0);
      setSelectedDateTime(newDateTime);
      onChange(newDateTime.toISOString());
    }
  };

  const handleConfirm = () => {
    if (selectedDateTime) {
      onChange(selectedDateTime.toISOString());
      setIsOpen(false);
      setShowTimePicker(false);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedDateTime(null);
    setSelectedTime({ hours: 0, minutes: 0 });
    onChange('');
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    if (!isDateDisabled(today)) {
      setSelectedDateTime(today);
      setSelectedTime({
        hours: today.getHours(),
        minutes: today.getMinutes()
      });
      setShowTimePicker(true);
    }
  };

  const generateHours = () => {
    const hours = [];
    for (let i = 0; i < 24; i++) {
      hours.push(i);
    }
    return hours;
  };

  const generateMinutes = () => {
    const minutes = [];
    for (let i = 0; i < 60; i += 15) {
      minutes.push(i);
    }
    return minutes;
  };

  const days = getDaysInMonth(currentMonth);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative" ref={dateTimePickerRef}>
        <div
          className={inputClasses}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          {...props}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Calendar className="w-4 h-4 text-gray-400 mr-2" />
              <span className={selectedDateTime ? 'text-gray-900' : 'text-gray-400'}>
                {formatDisplayDateTime(selectedDateTime)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {selectedDateTime && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              <ChevronLeft className="w-4 h-4 text-gray-400" />
            </div>
          </div>
        </div>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
            {!showTimePicker ? (
              <>
                {/* Calendar Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigateMonth(-1)}
                    className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4 text-gray-600" />
                  </button>
                  
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                    </h3>
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => navigateMonth(1)}
                    className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  </button>
                </div>

                {/* Calendar Body */}
                <div className="p-4">
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {dayNames.map(day => (
                      <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Days */}
                  <div className="grid grid-cols-7 gap-1">
                    {days.map((day, index) => {
                      if (!day) {
                        return <div key={index} className="h-8" />;
                      }

                      const isDisabled = isDateDisabled(day);
                      const isSelected = isDateSelected(day);
                      const isHovered = isDateHovered(day);
                      const isToday = day.toDateString() === new Date().toDateString();

                      return (
                        <button
                          key={day.toISOString()}
                          type="button"
                          onClick={() => handleDateSelect(day)}
                          onMouseEnter={() => setHoveredDate(day)}
                          onMouseLeave={() => setHoveredDate(null)}
                          disabled={isDisabled}
                          className={`
                            h-8 w-8 text-sm rounded-md transition-colors flex items-center justify-center
                            ${isDisabled 
                              ? 'text-gray-300 cursor-not-allowed' 
                              : 'hover:bg-primary-50 cursor-pointer'
                            }
                            ${isSelected 
                              ? 'bg-primary-600 text-white hover:bg-primary-700' 
                              : isHovered 
                                ? 'bg-primary-100 text-primary-700' 
                                : 'text-gray-700'
                            }
                            ${isToday && !isSelected ? 'bg-gray-100 font-semibold' : ''}
                          `}
                        >
                          {day.getDate()}
                        </button>
                      );
                    })}
                  </div>

                  {/* Today Button */}
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={goToToday}
                      disabled={isDateDisabled(new Date())}
                      className="w-full text-sm text-primary-600 hover:text-primary-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      Go to Today
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Time Picker Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowTimePicker(false)}
                    className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4 text-gray-600" />
                  </button>
                  
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center justify-center gap-2">
                      <Clock className="w-4 h-4" />
                      Select Time
                    </h3>
                  </div>
                  
                  <div className="w-8" />
                </div>

                {/* Time Picker Body */}
                <div className="p-4">
                  <div className="flex items-center justify-center gap-4">
                    {/* Hours */}
                    <div className="flex flex-col items-center">
                      <label className="text-xs font-medium text-gray-500 mb-2">Hour</label>
                      <div className="h-48 overflow-y-auto border border-gray-200 rounded-md">
                        {generateHours().map(hour => (
                          <button
                            key={hour}
                            type="button"
                            onClick={() => handleTimeChange(hour, selectedTime.minutes)}
                            className={`w-16 px-3 py-2 text-sm hover:bg-primary-50 transition-colors ${
                              selectedTime.hours === hour
                                ? 'bg-primary-600 text-white hover:bg-primary-700'
                                : 'text-gray-700'
                            }`}
                          >
                            {hour.toString().padStart(2, '0')}
                          </button>
                        ))}
                      </div>
                    </div>

                    <span className="text-2xl font-bold text-gray-400">:</span>

                    {/* Minutes */}
                    <div className="flex flex-col items-center">
                      <label className="text-xs font-medium text-gray-500 mb-2">Minute</label>
                      <div className="h-48 overflow-y-auto border border-gray-200 rounded-md">
                        {generateMinutes().map(minute => (
                          <button
                            key={minute}
                            type="button"
                            onClick={() => handleTimeChange(selectedTime.hours, minute)}
                            className={`w-16 px-3 py-2 text-sm hover:bg-primary-50 transition-colors ${
                              selectedTime.minutes === minute
                                ? 'bg-primary-600 text-white hover:bg-primary-700'
                                : 'text-gray-700'
                            }`}
                          >
                            {minute.toString().padStart(2, '0')}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Confirm Button */}
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleConfirm}
                      className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default DateTimePicker;

